/**
 * TimezoneContext - Reactive timezone state management
 * Provides timezone information throughout the app with automatic updates
 * Part of Week 3: Frontend Timezone Implementation
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const TimezoneContext = createContext({
  timezone: 'America/New_York',
  setTimezone: () => {},
  refreshTimezone: () => {},
  isLoading: false
});

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezoneState] = useState('America/New_York');
  const [isLoading, setIsLoading] = useState(false);

  // Load timezone from localStorage on mount
  useEffect(() => {
    const storedTimezone = localStorage.getItem('userTimezone');
    if (storedTimezone) {
      setTimezoneState(storedTimezone);
    } else {
      // Try to detect browser timezone as fallback
      try {
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTimezone) {
          setTimezoneState(browserTimezone);
          localStorage.setItem('userTimezone', browserTimezone);
        }
      } catch (error) {
        console.error('Failed to detect browser timezone:', error);
      }
    }
  }, []);

  /**
   * Update timezone - syncs with backend and localStorage
   * @param {string} newTimezone - IANA timezone string (e.g., 'America/New_York')
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const setTimezone = async (newTimezone) => {
    setIsLoading(true);

    try {
      // Update backend
      const token = localStorage.getItem('token');
      if (token) {
        await axios.put(
          `${API_BASE_URL}/auth/profile`,
          { timezone: newTimezone },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      // Update state
      setTimezoneState(newTimezone);
      localStorage.setItem('userTimezone', newTimezone);

      // Emit event for components that need to refresh
      window.dispatchEvent(
        new CustomEvent('timezone-changed', {
          detail: { timezone: newTimezone }
        })
      );

      setIsLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Failed to update timezone:', error);
      setIsLoading(false);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update timezone'
      };
    }
  };

  /**
   * Refresh timezone from localStorage
   * Useful after login or when user data is updated
   */
  const refreshTimezone = () => {
    const storedTimezone = localStorage.getItem('userTimezone');
    if (storedTimezone) {
      setTimezoneState(storedTimezone);
    }
  };

  const value = {
    timezone,
    setTimezone,
    refreshTimezone,
    isLoading
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * Custom hook to access timezone context
 * @returns {{timezone: string, setTimezone: function, refreshTimezone: function, isLoading: boolean}}
 * @throws {Error} If used outside TimezoneProvider
 */
export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
};

export default TimezoneContext;
